import type { SignalDataEndpoint, SignalsChartType } from "../../SignalsModels";
import { SignalsObjectType } from "../../SignalsModels";
import cardSizes from "./cardSizes.json";

/**
 * Every value that tunes the dashboard layout. Change a value here and the
 * grid, the size limits, the narrow view, the keyboard and the tests follow;
 * nothing else needs editing. Card sizes live in `cardSizes.json`, which the
 * server reads too.
 *
 * Grid sizes are in cells: one column of `GRID_COLUMNS`, one row of
 * `GRID_ROW_HEIGHT_PX`.
 */

// ---------------------------------------------------------------------------
// Grid units
// ---------------------------------------------------------------------------

/**
 * DO NOT CHANGE unless you intend to retune EVERYTHING below. Every height in
 * this file is a count of rows, so a different row height changes the pixel
 * size of every card's default, minimum and maximum, and of every saved
 * layout.
 *
 * The grid has no margin of its own. react-grid-layout adds its margin to every
 * row step (`h * rowHeight + (h - 1) * marginY`), so the gutter is padding
 * inside each grid item instead. Neighbours each contribute one gutter, so the
 * visible gap between cards is twice `GRID_GUTTER_PX`, and a card's visible
 * height is `h * GRID_ROW_HEIGHT_PX - 2 * GRID_GUTTER_PX`.
 */
export const GRID_ROW_HEIGHT_PX = 108 as const;

/**
 * DO NOT CHANGE unless you intend to retune EVERYTHING below and migrate every
 * saved layout. Saved layouts record this count, and a layout saved with a
 * different count fails validation and is discarded.
 */
export const GRID_COLUMNS = 6 as const;

export const GRID_GUTTER_PX = 8 as const;

/**
 * The tallest card, in rows, a saved layout may hold. The server rejects a
 * taller one (`DashboardLayout.MaximumHeight`). Only a card with no maximum of
 * its own, such as a text card, can be resized past it.
 */
export const MAXIMUM_SAVED_CARD_HEIGHT = 50;

/**
 * Below this viewport width the grid becomes the narrow view: cards in card
 * order, full width except small cards, and not arrangeable.
 */
export const GRID_BREAKPOINT_PX = 900 as const;

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

/**
 * The smallest plot a chart card may give its chart. The card's floor is this
 * plus its measured chrome (header, chips, controls, pagination, data grid).
 */
export const MINIMUM_CHART_PLOT_HEIGHT_PX = 200;

/**
 * The plot height an auto-height chart card fits to, which is the height
 * charts were drawn at before the grid existed.
 */
export const DEFAULT_CHART_PLOT_HEIGHT_PX = 300;

/**
 * A chart this many grid columns wide or narrower is compact, whatever the
 * container's pixel width, so the layout's cell count decides it on wide screens.
 */
export const COMPACT_CHART_COLUMNS = 3;

/**
 * The width below which a chart draws in its compact form. In pixels because
 * what matters is whether axis labels fit, which a column count cannot say.
 */
export const COMPACT_CHART_WIDTH_PX = 680;

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * How long the rendered layout has to hold still before an export captures
 * it. Longer than the grid's 200ms position transition
 * (`react-grid-layout/css/styles.css`), so no card is caught mid-slide.
 */
export const LAYOUT_SETTLE_QUIET_MS = 400;

/** The longest an export waits for the layout to settle before capturing anyway. */
export const LAYOUT_SETTLE_TIMEOUT_MS = 5000;

// ---------------------------------------------------------------------------
// Card sizes
// ---------------------------------------------------------------------------

/**
 * The sizes a card may take, in grid cells. A maximum is optional; a default
 * and a minimum are not.
 */
export type CardSizeSpec = {
    defaultWidth: number;
    defaultHeight: number;
    minimumWidth: number;
    minimumHeight: number;
    maximumWidth?: number;
    maximumHeight?: number;
    /**
     * The card's height is always its measured content and the user cannot
     * change it. Only the width is resizable.
     */
    heightFollowsContent?: boolean;
};

/**
 * The sizes for each card type, from `cardSizes.json`, which the server also
 * reads to place new cards. Change sizes there, not here. Defaults reproduce
 * the size each type rendered at before the grid existed. For content-driven
 * types the minimum height is only a floor; the measured content decides the
 * real height.
 *
 * Why some values are what they are:
 * - Chart: a chart's computed floor is its chrome plus a 200px plot, which is
 *   already four rows for most charts, so the default is four. Three columns
 *   is the narrowest a chart draws compact (`COMPACT_CHART_COLUMNS`).
 * - SmallCard: the stat tile the grid is proportioned around, one cell high.
 * - Table: locked to its default size for now. Content can still push it
 *   taller.
 */
export const CARD_SIZE_SPECS: Readonly<
    Record<SignalsObjectType, Readonly<CardSizeSpec>>
> = {
    [SignalsObjectType.Chart]: cardSizes.cardTypes.Chart,
    [SignalsObjectType.TextCard]: cardSizes.cardTypes.TextCard,
    [SignalsObjectType.SmallCard]: cardSizes.cardTypes.SmallCard,
    [SignalsObjectType.MontageCard]: cardSizes.cardTypes.MontageCard,
    [SignalsObjectType.Table]: cardSizes.cardTypes.Table,
    [SignalsObjectType.Unique]: cardSizes.cardTypes.Unique,
};

/**
 * Overrides for one chart type, from `cardSizes.json`, applied on top of the
 * chart sizes. Only the fields given change. A donut stays legible at two
 * columns.
 */
export const CHART_TYPE_SIZE_OVERRIDES: Readonly<
    Partial<Record<SignalsChartType, Partial<CardSizeSpec>>>
> = cardSizes.chartTypeOverrides;

/**
 * Overrides for every card that reads one data endpoint, from
 * `cardSizes.json`, applied last, on top of the card type's sizes and any
 * chart type override. Only the fields given change. An endpoint some card
 * types share, such as `ExtractedConversationData`, is overridden for all of
 * them.
 *
 * For example, to let a wide table narrow and stop a donut growing tall:
 *
 *     "ExtractedConversationData": { "minimumWidth": 3 },
 *     "TotalConversationsDonut": { "maximumHeight": 4 }
 */
export const ENDPOINT_SIZE_OVERRIDES: Readonly<
    Partial<Record<SignalDataEndpoint, Partial<CardSizeSpec>>>
> = cardSizes.endpointOverrides;
